import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { InterestButton } from "../InterestButton";
import { ShareButton } from "../ShareButton";

export const Hero = ({ onApply }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden"
      style={{ backgroundColor: "#0E0A16" }}
    >
      {/* Parallax radial glow */}
      <motion.div
        aria-hidden="true"
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-[10%] left-1/2 -translate-x-1/2 w-[900px] h-[900px] max-w-[120vw]"
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background:
              "radial-gradient(circle at center, rgba(199,36,177,0.22) 0%, rgba(199,36,177,0.05) 35%, transparent 62%)",
          }}
        />
      </motion.div>

      {/* Slow-rotating ring accent */}
      <motion.div
        aria-hidden="true"
        animate={{ rotate: 360 }}
        transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
        className="pointer-events-none absolute right-[-180px] top-[18%] w-[420px] h-[420px] rounded-full hidden lg:block"
        style={{ border: "1px solid #2A2233" }}
      />

      {/* Faint vertical guide lines */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 mx-auto max-w-[1400px] px-6 md:px-10 hidden md:block">
        <div className="h-full w-full grid grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ borderLeft: "1px solid rgba(42,34,51,0.4)" }} />
          ))}
        </div>
      </div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 mx-auto w-full max-w-[1000px] px-6 md:px-10 flex flex-col items-center text-center">
        {/* Wordmark centrepiece — single SVG, masked reveal, scaled via CSS */}
        <div className="overflow-hidden pb-[0.14em] -mb-[0.14em]">
          <motion.img
            src="/logo.svg"
            alt="VYNLWORLD"
            data-testid="hero-heading"
            draggable="false"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
            className="block h-auto select-none"
            style={{ width: "clamp(260px, 60vw, 720px)" }}
          />
        </div>

        {/* Supporting line (replaces the old headline) */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 font-inter font-light uppercase"
          style={{ color: "#9B8FAC", fontSize: "clamp(11px, 1.1vw, 12px)", letterSpacing: "0.28em" }}
        >
          Custom made&nbsp;·&nbsp;Approved music artists' merch
        </motion.p>

        {/* Hairline divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.78, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 h-px w-14"
          style={{ backgroundColor: "#2A2233" }}
        />

        {/* Interest + Share — quiet, icon-led, centred below with whitespace */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95, duration: 0.9 }}
          className="mt-10 flex items-center justify-center gap-10"
        >
          <InterestButton onApply={onApply} testid="hero-interest-btn" quiet />
          <ShareButton testid="hero-share-btn" quiet />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
